import type {
  ResendScratchCodeMutation,
  ScratchErrorReason,
  SubmitScratchEmailInput,
  SubmitScratchEmailMutation,
  SubmitScratchPhoneInput,
  SubmitScratchPhoneMutation,
} from '#graphql-operations'

/**
 * Scratch to win: the four write calls, each reduced to "it worked, here is the
 * progress" or "it was refused, here is the reason".
 *
 * Nothing here ever surfaces the server's `message` — Vendure answers in
 * English only and these strings reach a Czech, Slovak, Polish or German
 * shopper — so the caller branches on `reason` and translates (see
 * `scratchErrorPresentation`). Nothing here logs an e-mail address, a phone
 * number, a lead token or a code either: the reason is the whole log line.
 *
 * None of these touch the order, so they deliberately skip `useRequestQueue`
 * (which serialises order writes); the flow's own `submitting` flag is what
 * stops a double submit.
 */

export type ScratchFailure = ScratchErrorReason | 'NETWORK' | 'UNKNOWN'

export interface ScratchProgressResult {
  leadToken: string | null
  nextStep: 'PHONE' | 'DONE'
  codeSent: boolean
}

export type ScratchSubmitOutcome
  = | { ok: true, progress: ScratchProgressResult }
    | { ok: false, reason: ScratchFailure }

export interface ScratchAudienceResult {
  show: boolean
  skipEmail: boolean
  reason: string | null
}

type AnyScratchResult
  = | SubmitScratchEmailMutation['submitScratchEmail']
    | SubmitScratchPhoneMutation['submitScratchPhone']
    | ResendScratchCodeMutation['resendScratchCode']
    | undefined

/** Both union members, judged by `__typename`; anything else is an unknown shape. */
function outcomeOf(name: string, result: AnyScratchResult): ScratchSubmitOutcome {
  if (isGraphqlType(result, 'ScratchProgress')) {
    return {
      ok: true,
      progress: {
        leadToken: result.leadToken ?? null,
        nextStep: result.nextStep,
        codeSent: result.codeSent,
      },
    }
  }

  if (isGraphqlType(result, 'ScratchError')) {
    console.warn(`${name} refused:`, result.reason)
    return { ok: false, reason: result.reason }
  }

  return { ok: false, reason: 'UNKNOWN' }
}

export function useScratchSubmit(): {
  submitEmail: (input: SubmitScratchEmailInput) => Promise<ScratchSubmitOutcome>
  submitPhone: (input: SubmitScratchPhoneInput) => Promise<ScratchSubmitOutcome>
  resendCode: (leadToken: string | null) => Promise<ScratchSubmitOutcome>
  fetchAudience: () => Promise<ScratchAudienceResult | null>
} {
  async function submitEmail(input: SubmitScratchEmailInput): Promise<ScratchSubmitOutcome> {
    try {
      const { data } = await useGraphqlMutation('submitScratchEmail', { input })
      // Typed by hand: the middleware's `data` is `any` here, and narrowing
      // `any` through the two type guards collapses to `never` in the second
      // branch (see `useGiftSelection`).
      const result: SubmitScratchEmailMutation['submitScratchEmail'] | undefined = data?.submitScratchEmail
      return outcomeOf('submitScratchEmail', result)
    }
    catch (error) {
      console.error('submitScratchEmail failed', error)
      return { ok: false, reason: 'NETWORK' }
    }
  }

  async function submitPhone(input: SubmitScratchPhoneInput): Promise<ScratchSubmitOutcome> {
    try {
      const { data } = await useGraphqlMutation('submitScratchPhone', { input })
      const result: SubmitScratchPhoneMutation['submitScratchPhone'] | undefined = data?.submitScratchPhone
      return outcomeOf('submitScratchPhone', result)
    }
    catch (error) {
      console.error('submitScratchPhone failed', error)
      return { ok: false, reason: 'NETWORK' }
    }
  }

  async function resendCode(leadToken: string | null): Promise<ScratchSubmitOutcome> {
    try {
      const { data } = await useGraphqlMutation('resendScratchCode', { leadToken })
      const result: ResendScratchCodeMutation['resendScratchCode'] | undefined = data?.resendScratchCode
      return outcomeOf('resendScratchCode', result)
    }
    catch (error) {
      console.error('resendScratchCode failed', error)
      return { ok: false, reason: 'NETWORK' }
    }
  }

  /** Null means "we do not know" — the caller must not read that as "may play". */
  async function fetchAudience(): Promise<ScratchAudienceResult | null> {
    try {
      const { data } = await useGraphqlQuery('scratchAudience')
      const audience = data?.scratchAudience
      if (!audience)
        return null
      return { show: audience.show, skipEmail: audience.skipEmail, reason: audience.reason ?? null }
    }
    catch (error) {
      console.error('scratchAudience failed', error)
      return null
    }
  }

  return { submitEmail, submitPhone, resendCode, fetchAudience }
}

/** Codes already confirmed in this page life. */
const confirmed = new Set<string>()

/**
 * Tells the backend the mailbox behind a prize code has been proven — the
 * e-mail link was opened, or the code came back from the cookie on a later page
 * load. Standalone (like `previewCoupon`) so the `?code=` carrier can call it
 * outside component setup. Never throws.
 *
 * Resolves `true` only when the call got through: the backend answers `true` to
 * every storefront request whatever the code, so the answer says nothing about
 * the lead — but a lost call throws, and one without the proxy key answers
 * `false`. `useUrlCoupon` records a `true` beside the prize verdict and stops
 * sending; until then it retries on the next page load, because
 * `emailConfirmedAt` gates the Ecomail push, the SMS export and the 30-day
 * unconfirmed purge, and a call lost to a network blip must not leave the lead
 * unconfirmed for good. `false` too for a call this page life already made.
 */
export async function confirmScratchCode(couponCode: string): Promise<boolean> {
  // Browser only, structurally. The `?code=` carrier's validation half runs
  // during SSR since the plugin stopped being client-only, and a page render
  // must not send a mutation. The carrier also skips prize codes on the server,
  // but that test is a string prefix kept in step with the backend by hand:
  // this guard is what makes the rule hold when the prefix ever moves. It also
  // keeps `confirmed` — module state — from filling up across requests.
  if (!import.meta.client)
    return false
  const code = couponCode.trim()
  const key = code.toUpperCase()
  if (!code || confirmed.has(key))
    return false
  confirmed.add(key)
  try {
    const { data } = await useGraphqlMutation('confirmScratchCode', { couponCode: code })
    return data?.confirmScratchCode === true
  }
  catch (error) {
    console.warn('confirmScratchCode failed', error)
    return false
  }
}
